import {
  computed, nextTick, onBeforeUnmount, ref,
} from 'vue';
import { captureException } from '@sentry/vue';
import { trackEvent } from '../utils/analytics';
import { t } from '../i18n';
import formatTimer from '../utils/demoViewer/formatTimer';
import { rasterizeViewer } from '../utils/demoViewer/capture/rasterizeSvg';
import { drawHud } from '../utils/demoViewer/capture/drawHud';
import { drawWatermark } from '../utils/demoViewer/capture/watermark';
import { CAPTURE_CONFIG } from '../utils/demoViewer/capture/config';
import { planGifFrames } from '../utils/demoViewer/capture/gifPlan';

const TRANSPARENT = ['', 'transparent', 'rgba(0, 0, 0, 0)'];

// The first painted background behind the viewer, so the PNG is not transparent.
function backgroundOf(element) {
  if (!element) return '#000';
  const color = window.getComputedStyle(element).backgroundColor;
  return TRANSPARENT.includes(color) ? backgroundOf(element.parentElement) : color;
}

function download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function toPng(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Canvas gave no PNG'));
    }, 'image/png');
  });
}

/**
 * A PNG of the 2D viewer as it is on screen,
 * HUD and watermark included. Never throws into the page and never touches playback.
 * The GIF export seeks through frames with seekFrame(index) and restores the viewer after.
 */
// eslint-disable-next-line import-x/prefer-default-export
export function useViewerCapture({
  route, store, viewerCanvas, canvasContainer, svgWidth, svgHeight, mapInfo, currentRound,
  currentTick, bombPlantEvent, roundEnded, activeKills, getTimeOfTick, isUnavailable,
  roundData, currentFrame, isPlaying, stopPlay, resumePlay, seekFrame,
}) {
  const capturing = ref(false);
  const gifExporting = ref(false);
  const canCopy = Boolean(navigator.clipboard?.write && window.ClipboardItem);
  const captureDisabled = computed(() => capturing.value || gifExporting.value || isUnavailable());

  // The same rule ViewerHud.vue uses to show the bomb timer.
  function hudTimer() {
    const isBombTimer = Boolean(bombPlantEvent.value
      && bombPlantEvent.value.tick < currentTick.value && !roundEnded.value);
    const seconds = getTimeOfTick(currentTick.value);
    return { seconds, isBombTimer, timerText: formatTimer(seconds, isBombTimer) };
  }

  function toast(messageType, messageText) {
    store.commit('showMessage', { messageType, messageText });
    setTimeout(() => store.commit('hideMessage'), 3000);
  }

  async function renderPng() {
    const width = svgWidth.value;
    const height = svgHeight.value;
    const scale = Math.min(window.devicePixelRatio || 1, 2);
    const { seconds, isBombTimer, timerText } = hudTimer();

    const canvas = await rasterizeViewer({
      svg: viewerCanvas.value.svgCanvas,
      overlay: viewerCanvas.value.drawingOverlay || null,
      width,
      height,
      scale,
      background: backgroundOf(canvasContainer.value),
    });
    const ctx = canvas.getContext('2d');
    await drawHud(ctx, {
      round: currentRound.value, timerText, isBombTimer, kills: activeKills.value, width, scale,
    });
    await drawWatermark(ctx, { width, height, scale });

    const [mins, secs] = formatTimer(seconds, false).split(':');
    const filename = `refrag-${mapInfo.value?.name}-r${currentRound.value}-${mins}m${secs}s.png`;
    return {
      blob: await toPng(canvas), filename, width: canvas.width, height: canvas.height,
    };
  }

  // method: 'download' | 'clipboard' | 'shortcut' (a shortcut downloads).
  async function takeScreenshot({ method }) {
    if (captureDisabled.value) return;
    capturing.value = true;
    const started = performance.now();
    const properties = {
      viewer_mode: '2d',
      method,
      map: mapInfo.value?.name ?? null,
      round: currentRound.value,
      demo_id: route.params.uuid ?? route.params.id,
    };

    try {
      await nextTick();
      const {
        blob, filename, width, height,
      } = await renderPng();
      const encodeMs = performance.now() - started;
      if (method === 'clipboard') {
        await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
      } else {
        download(blob, filename);
      }
      trackEvent('viewer_screenshot_taken', {
        ...properties, success: true, error: null, encode_ms: encodeMs, width, height,
      });
      toast('success', method === 'clipboard' ? t('shared.viewer_capture.copied') : t('shared.viewer_capture.saved'));
    } catch (error) {
      captureException(error);
      trackEvent('viewer_screenshot_taken', {
        ...properties,
        success: false,
        error: error?.name ?? 'Error',
        encode_ms: performance.now() - started,
        width: null,
        height: null,
      });
      toast('error', t('shared.viewer_capture.screenshot_failed'));
    } finally {
      capturing.value = false;
    }
  }

  // ---- GIF export: the visible viewer steps through the range, then is restored. ----

  const gifProgress = ref('');
  let gifJob = null;

  function planFor({ lengthS, placement }) {
    return planGifFrames(roundData.value.frames, currentFrame.value, {
      lengthS, placement, fps: CAPTURE_CONFIG.gif.fps,
    });
  }

  function clockAt(frameIndex) {
    return getTimeOfTick(roundData.value.frames[frameIndex].tick);
  }

  // The round-clock range a GIF would cover, e.g. "1:12 → 1:07".
  function gifRangeText(options) {
    if (!roundData.value?.frames?.length) return '';
    const plan = planFor(options);
    const from = formatTimer(clockAt(plan[0]), false);
    return `${from} → ${formatTimer(clockAt(plan[plan.length - 1]), false)}`;
  }

  async function renderGifFrame(width, height, scale) {
    const { isBombTimer, timerText } = hudTimer();
    const canvas = await rasterizeViewer({
      svg: viewerCanvas.value.svgCanvas,
      overlay: viewerCanvas.value.drawingOverlay || null,
      width,
      height,
      scale,
      background: backgroundOf(canvasContainer.value),
    });
    const ctx = canvas.getContext('2d');
    await drawHud(ctx, {
      round: currentRound.value, timerText, isBombTimer, kills: activeKills.value, width, scale,
    });
    await drawWatermark(ctx, { width, height, scale });
    return ctx.getImageData(0, 0, canvas.width, canvas.height);
  }

  // options: { lengthS: 3 | 5 | 10, placement: 'centred' | 'starting' }
  async function exportGif(options) {
    if (captureDisabled.value || gifJob) return;
    const { fps, width: gifWidth } = CAPTURE_CONFIG.gif;
    const frameBefore = currentFrame.value;
    const wasPlaying = isPlaying();
    const plan = planFor(options);
    const width = svgWidth.value;
    const height = svgHeight.value;
    const scale = gifWidth / width;
    const started = performance.now();
    let renderMs = 0;
    const [mins, secs] = formatTimer(clockAt(plan[0]), false).split(':');
    const filename = `refrag-${mapInfo.value?.name}-r${currentRound.value}-${mins}m${secs}s.gif`;
    const properties = {
      viewer_mode: '2d',
      duration_s: plan.length / fps,
      fps,
      frames: plan.length,
      width: gifWidth,
      height: Math.round(height * scale),
      map: mapInfo.value?.name ?? null,
      round: currentRound.value,
      demo_id: route.params.uuid ?? route.params.id,
    };

    const job = {
      cancelled: false, failure: null, bytes: null, inFlight: 0, notify: () => {},
    };
    gifJob = job;
    gifExporting.value = true;
    stopPlay();

    // Resolves once ready() holds, or the job is cancelled or failed.
    const waitFor = (ready) => new Promise((resolve) => {
      job.notify = () => {
        if (ready() || job.cancelled || job.failure) resolve();
      };
      job.notify();
    });

    try {
      job.worker = new Worker(
        new URL('../workers/gifEncoder.worker.js', import.meta.url),
        { type: 'module' },
      );
      job.worker.onmessage = ({ data }) => {
        if (data.type === 'encoded') job.inFlight -= 1;
        if (data.type === 'done') job.bytes = data.bytes;
        if (data.type === 'error') {
          job.failure = new Error(data.message);
          job.failure.name = data.name;
        }
        job.notify();
      };
      job.worker.onerror = (event) => {
        job.failure = new Error(event.message || 'GIF worker failed');
        job.notify();
      };

      for (let i = 0; i < plan.length; i += 1) {
        // At most 4 frames wait in the worker, so a slow encoder never holds hundreds of MB.
        // eslint-disable-next-line no-await-in-loop
        await waitFor(() => job.inFlight < 4);
        if (job.cancelled || job.failure) break;
        gifProgress.value = t('shared.viewer_capture.rendering_frame', { current: i + 1, total: plan.length });
        seekFrame(plan[i]);
        // eslint-disable-next-line no-await-in-loop
        await nextTick();
        // eslint-disable-next-line no-await-in-loop
        const image = await renderGifFrame(width, height, scale);
        if (job.cancelled) break;
        job.inFlight += 1;
        job.worker.postMessage({
          type: 'frame', data: image.data, width: image.width, height: image.height, fps,
        }, [image.data.buffer]);
      }
      renderMs = performance.now() - started;
      if (!job.cancelled && !job.failure) {
        gifProgress.value = t('shared.viewer_capture.encoding');
        job.worker.postMessage({ type: 'finish' });
        await waitFor(() => job.bytes);
      }
      if (job.failure) throw job.failure;

      const bytes = job.cancelled ? 0 : job.bytes.byteLength;
      if (!job.cancelled) download(new Blob([job.bytes], { type: 'image/gif' }), filename);
      trackEvent('viewer_gif_exported', {
        ...properties,
        success: !job.cancelled,
        cancelled: job.cancelled,
        error: null,
        bytes,
        render_ms: renderMs,
        encode_ms: performance.now() - started - renderMs,
      });
      if (!job.cancelled) toast('success', t('shared.viewer_capture.gif_saved'));
    } catch (error) {
      captureException(error);
      trackEvent('viewer_gif_exported', {
        ...properties,
        success: false,
        cancelled: false,
        error: error?.name ?? 'Error',
        bytes: 0,
        render_ms: renderMs || performance.now() - started,
        encode_ms: renderMs ? performance.now() - started - renderMs : 0,
      });
      toast('error', t('shared.viewer_capture.gif_failed'));
    } finally {
      job.worker?.terminate();
      gifJob = null;
      seekFrame(frameBefore);
      if (wasPlaying) resumePlay();
      gifExporting.value = false;
      gifProgress.value = '';
    }
  }

  function cancelGif() {
    if (!gifJob) return;
    gifJob.cancelled = true;
    gifJob.worker?.terminate();
    gifJob.notify();
  }

  // Closing the page mid-export stops the encoder.
  onBeforeUnmount(cancelGif);

  return {
    canCopy,
    captureDisabled,
    takeScreenshot,
    gifExporting,
    gifProgress,
    gifRangeText,
    exportGif,
    cancelGif,
  };
}
